'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "slots", "slotProps", "style"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { getChatLayoutPaneKind } from "./internals/chatLayoutPaneKind.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function assignPaneChild(paneChildren, kind, child) {
  if (!paneChildren[kind]) {
    paneChildren[kind] = [];
  }
  paneChildren[kind].push(child);
}
function resolvePaneChildren(children) {
  const allChildren = React.Children.toArray(children);
  const paneChildren = {
    conversations: [],
    thread: []
  };
  const unassignedChildren = [];
  allChildren.forEach(child => {
    const paneKind = getChatLayoutPaneKind(child);
    if (paneKind === null) {
      unassignedChildren.push(child);
      return;
    }
    assignPaneChild(paneChildren, paneKind, child);
  });
  if (allChildren.length <= 1) {
    const singleChild = paneChildren.conversations[0] ?? paneChildren.thread[0] ?? unassignedChildren[0];
    if (singleChild === undefined) {
      return paneChildren;
    }
    if (paneChildren.conversations.length > 0) {
      paneChildren.thread = [];
      return {
        conversations: [singleChild],
        thread: []
      };
    }
    if (paneChildren.thread.length > 0) {
      paneChildren.conversations = [];
      return {
        conversations: [],
        thread: [singleChild]
      };
    }
    return {
      conversations: [],
      thread: [singleChild]
    };
  }
  unassignedChildren.forEach(child => {
    if (paneChildren.conversations.length === 0) {
      assignPaneChild(paneChildren, 'conversations', child);
      return;
    }
    assignPaneChild(paneChildren, 'thread', child);
  });
  if (process.env.NODE_ENV !== 'production' && unassignedChildren.length > 0 && unassignedChildren.length < allChildren.length) {
    console.warn('MUI X Chat: ChatLayout could not determine the pane kind for some children. ' + 'Use the `pane` prop (pane="conversations" or pane="thread") to explicitly assign children to panes, ' + 'or use the `ConversationListRoot` and `ConversationRoot` components directly.');
  }
  return paneChildren;
}
export const ChatLayout = /*#__PURE__*/React.forwardRef(function ChatLayout(props, ref) {
  const {
      children,
      slots,
      slotProps,
      style
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const paneChildren = resolvePaneChildren(children);
  const ownerState = {
    hasConversationsPane: paneChildren.conversations.length > 0,
    hasThreadPane: paneChildren.thread.length > 0
  };
  const Root = slots?.root ?? 'div';
  const ConversationsPane = slots?.conversationsPane ?? 'div';
  const ThreadPane = slots?.threadPane ?? 'div';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref,
      style: _extends({
        display: 'flex'
      }, style)
    }
  });
  const conversationsPaneProps = useSlotProps({
    elementType: ConversationsPane,
    externalSlotProps: slotProps?.conversationsPane,
    ownerState: {
      pane: 'conversations'
    },
    additionalProps: {
      style: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden'
      }
    }
  });
  const threadPaneProps = useSlotProps({
    elementType: ThreadPane,
    externalSlotProps: slotProps?.threadPane,
    ownerState: {
      pane: 'thread'
    },
    additionalProps: {
      style: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden'
      }
    }
  });
  return /*#__PURE__*/_jsxs(Root, _extends({}, rootProps, {
    children: [ownerState.hasConversationsPane ? /*#__PURE__*/_jsx(ConversationsPane, _extends({}, conversationsPaneProps, {
      children: paneChildren.conversations
    })) : null, ownerState.hasThreadPane ? /*#__PURE__*/_jsx(ThreadPane, _extends({}, threadPaneProps, {
      children: paneChildren.thread
    })) : null]
  }));
});
if (process.env.NODE_ENV !== "production") ChatLayout.displayName = "ChatLayout";